/**
 * Golden Set AI — UI for managing and running AI evaluation tests.
 */

import { api, $, toast, escHtml, _ensureMachineCache, _allMachinesCache, formatDateTimeRO } from './core';
import { askConfirm } from './dialogs';

interface GsItem {
    id: string;
    question: string;
    machine_id: string;
    line?: string;
    chapter_type: string;
    expected_keywords: string[];
    expected_answer: string;
    expected_source: string;
    difficulty: string;
}

interface GsResult {
    id: string;
    question: string;
    machine_id: string;
    line?: string;
    chapter_type: string;
    difficulty: string;
    answer_snippet: string;
    score: number;
    grade: string;
    found_keywords: string[];
    missing_keywords: string[];
    elapsed_s: number;
    timestamp: string;
}

const CHAPTER_LABELS: Record<string, string> = {
    maintenance: 'Mentenanță',
    error_codes: 'Coduri eroare',
    adjustment: 'Reglaje',
    spare_parts: 'Piese schimb',
    safety: 'Siguranță',
    operation: 'Operare',
};

const DIFF_LABELS: Record<string, string> = {
    easy: '🟢 Easy', medium: '🟡 Medium', hard: '🔴 Hard',
};

let _gsItems: GsItem[] = [];
let _gsResults: GsResult[] = [];
let _pollTimer: any = null;

/* ═══ INIT ══════════════════════════════════════════════════ */

export async function initGoldenSet(): Promise<void> {
    _initTabs();
    await _ensureMachineCache();
    _populateMachineSelects();
    _initEvents();
    await _loadQuestions();
    await _loadLastResults();
}

function _initTabs(): void {
    document.querySelectorAll('.gs-tab').forEach(tab => {
        tab.addEventListener('click', () => {
            document.querySelectorAll('.gs-tab').forEach(t => {
                (t as HTMLElement).style.borderBottomColor = 'transparent';
                (t as HTMLElement).style.color = 'var(--text-muted)';
                t.classList.remove('active');
            });
            document.querySelectorAll('#sec-golden-set .gs-panel').forEach(p => {
                p.classList.remove('active');
                (p as HTMLElement).style.display = 'none';
            });
            tab.classList.add('active');
            (tab as HTMLElement).style.borderBottomColor = 'var(--accent)';
            (tab as HTMLElement).style.color = 'var(--accent)';
            const target = (tab as HTMLElement).dataset.gstab;
            if (target) {
                const panel = document.getElementById(target);
                if (panel) {
                    panel.classList.add('active');
                    panel.style.display = 'block';
                }
            }
            if (target === 'gs-panel-results') _renderResults();
            if (target === 'gs-panel-monitoring') _loadAiMonitoring();
            if (target === 'gs-panel-validation') _loadFeedbackValidation();
        });
    });
}

/* ═══ FEEDBACK VALIDATION (master) ══════════════════════════ */

async function _loadFeedbackValidation(): Promise<void> {
    const statusEl = document.getElementById('fv-filter-status') as HTMLSelectElement | null;
    const voteEl = document.getElementById('fv-filter-vote') as HTMLSelectElement | null;
    const list = document.getElementById('fv-list');
    const sum = document.getElementById('fv-summary');
    if (!list) return;

    // Attach filter handlers once
    if (!(list as any)._fvInit) {
        (list as any)._fvInit = true;
        statusEl?.addEventListener('change', _loadFeedbackValidation);
        voteEl?.addEventListener('change', _loadFeedbackValidation);
    }

    const status = statusEl?.value || 'pending';
    const vote = voteEl?.value || '';
    list.innerHTML = '<div style="padding:20px;color:var(--text-muted);text-align:center">Se încarcă…</div>';
    let data: any;
    try {
        const qs = new URLSearchParams({ status });
        if (vote) qs.set('vote', vote);
        data = await api('/api/ai_feedback/pending?' + qs.toString());
    } catch (err: any) {
        list.innerHTML = `<div style="color:var(--red);padding:14px">Eroare: ${escHtml(err?.message || err)}</div>`;
        return;
    }

    if (sum) {
        const s = data.summary || {};
        sum.textContent = `Total — pending: ${s.pending || 0}, approved: ${s.approved || 0}, rejected: ${s.rejected || 0}`;
    }

    const items = data.items || [];
    if (items.length === 0) {
        list.innerHTML = '<div style="padding:20px;color:var(--text-muted);text-align:center">Niciun item cu acest filtru.</div>';
        return;
    }

    list.innerHTML = items.map((it: any) => {
        const voteIcon = it.vote === 'good' ? '👍' : it.vote === 'bad' ? '👎' : '✏️';
        const voteColor = it.vote === 'good' ? 'var(--green)' : it.vote === 'bad' ? 'var(--red)' : '#4f46e5';
        const statusBadge = {
            pending: '<span style="background:#f59e0b;color:#fff;padding:2px 8px;border-radius:10px;font-size:.72rem">În așteptare</span>',
            approved: '<span style="background:var(--green);color:#fff;padding:2px 8px;border-radius:10px;font-size:.72rem">Aprobat</span>',
            rejected: '<span style="background:var(--red);color:#fff;padding:2px 8px;border-radius:10px;font-size:.72rem">Respins</span>',
        }[it.validation_status as string] || '';
        const created = (it.created_at || '').substring(0, 16);
        const validated = it.validated_by ? `✓ ${escHtml(it.validated_by)} la ${escHtml((it.validated_at || '').substring(0, 16))}` : '';
        const showActions = it.validation_status === 'pending';
        return `<div class="fv-card" id="fv-card-${it.id}" style="background:var(--surface);border:1px solid var(--border);border-radius:8px;padding:12px">
            <div style="display:flex;gap:10px;align-items:center;margin-bottom:8px;flex-wrap:wrap">
                <span style="font-size:1.1rem;color:${voteColor}">${voteIcon}</span>
                ${statusBadge}
                <span style="font-size:.72rem;color:var(--text-muted)">${escHtml(it.user_role || '?')}</span>
                <span style="font-size:.72rem;color:var(--text-muted)">${escHtml(it.ai_source || '')}</span>
                <span style="font-size:.72rem;color:var(--text-muted);margin-left:auto">${escHtml(created)}</span>
            </div>
            <div style="font-size:.85rem;margin-bottom:6px"><strong>Întrebare:</strong> ${escHtml((it.question || '').substring(0, 300))}</div>
            <div style="font-size:.85rem;margin-bottom:6px;color:var(--text-muted)"><strong>Răspuns AI:</strong> ${escHtml((it.answer || '').substring(0, 400))}</div>
            ${it.correction ? `<div style="font-size:.85rem;margin-bottom:8px;background:rgba(99,102,241,.08);padding:8px;border-radius:6px"><strong>Corecție propusă:</strong><br>${escHtml(it.correction)}</div>` : ''}
            ${validated ? `<div style="font-size:.75rem;color:var(--text-muted);margin-top:6px">${validated}</div>` : ''}
            ${showActions ? `
            <div style="display:flex;gap:6px;margin-top:8px;flex-wrap:wrap">
                <textarea class="fv-override" placeholder="(Opțional) editează corecția înainte de aprobare..." style="width:100%;min-height:50px;padding:6px;border:1px solid var(--border);border-radius:4px;font-family:inherit;font-size:.82rem;background:var(--bg);color:var(--text)">${escHtml(it.correction || '')}</textarea>
                <button class="btn btn-sm fv-approve" style="background:var(--green);color:#fff" data-id="${it.id}">✓ Aprobă</button>
                <button class="btn btn-sm fv-reject" style="background:var(--red);color:#fff" data-id="${it.id}">✗ Respinge</button>
            </div>` : ''}
        </div>`;
    }).join('');

    list.querySelectorAll('.fv-approve').forEach(btn => {
        btn.addEventListener('click', async (e) => {
            const id = (e.currentTarget as HTMLElement).dataset.id;
            if (!id) return;
            const card = document.getElementById(`fv-card-${id}`);
            const override = (card?.querySelector('.fv-override') as HTMLTextAreaElement | null)?.value?.trim();
            await _validateFeedback(parseInt(id), 'approve', override);
        });
    });
    list.querySelectorAll('.fv-reject').forEach(btn => {
        btn.addEventListener('click', async (e) => {
            const id = (e.currentTarget as HTMLElement).dataset.id;
            if (!id) return;
            await _validateFeedback(parseInt(id), 'reject');
        });
    });
}

async function _validateFeedback(id: number, action: 'approve' | 'reject', correctionOverride?: string): Promise<void> {
    try {
        await api(`/api/ai_feedback/${id}/validate`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ action, correction_override: correctionOverride || '' }),
        });
        toast(action === 'approve' ? 'Corecție aprobată — va intra în fine-tuning' : 'Corecție respinsă', 'success');
        await _loadFeedbackValidation();
    } catch (err: any) {
        toast(`Eroare: ${err?.message || err}`, 'error');
    }
}

/* ═══ AI MONITORING DASHBOARD ═══════════════════════════════ */

async function _loadAiMonitoring(): Promise<void> {
    const loading = document.getElementById('ai-mon-loading');
    const content = document.getElementById('ai-mon-content');
    if (loading) loading.style.display = 'block';
    if (content) (content as HTMLElement).style.display = 'none';

    let data: any;
    try {
        data = await api('/api/ai/monitoring');
    } catch (err: any) {
        if (loading) loading.innerHTML = `<span style="color:var(--red)">Eroare la încărcare: ${escHtml(err?.message || err)}</span>`;
        return;
    }
    if (loading) loading.style.display = 'none';
    if (content) (content as HTMLElement).style.display = 'block';

    // Alerts
    const alertsEl = document.getElementById('ai-mon-alerts');
    if (alertsEl) {
        if (data.alerts && data.alerts.length > 0) {
            alertsEl.innerHTML = data.alerts.slice(0, 3).map((a: any) =>
                `<div style="background:rgba(220,38,38,.12);border-left:3px solid var(--red);padding:10px 12px;margin-bottom:6px;border-radius:4px">
                    <strong>${escHtml(a.title || 'Alertă AI')}</strong>
                    <span style="font-size:.78rem;color:var(--text-muted);margin-left:8px">${escHtml((a.created_at || '').substring(0, 16))}</span>
                    <div style="font-size:.85rem;margin-top:3px">${escHtml(a.body || '')}</div>
                </div>`
            ).join('');
        } else {
            alertsEl.innerHTML = '';
        }
    }

    // KPI Golden Set
    const gsHist = data.golden_set_history || [];
    const lastGs = gsHist[gsHist.length - 1];

    // Audit 2.23 — diferența se calcula între ULTIMELE DOUĂ rulări, oricare ar
    // fi ele. Pe 18 august asta a însemnat „77.6%, minus 1.7 puncte" comparând o
    // rulare moartă la jumătate (21/29, model local) cu una completă de alt tip
    // (70/94, prin lanțul aplicației). Regula: se compară doar rulări care
    // măsoară același lucru — același traseu, același număr de întrebări,
    // aceleași filtre, aceeași versiune de set — și niciodată una întreruptă.
    const _comparabile = (a: any, b: any): boolean => {
        if (!a || !b) return false;
        if (a.rulare_incompleta || b.rulare_incompleta) return false;
        if ((a.mod || 'necunoscut') !== (b.mod || 'necunoscut')) return false;
        if ((a.total ?? null) !== (b.total ?? null)) return false;
        if ((a.filtre || 'complet') !== (b.filtre || 'complet')) return false;
        if (a.set_versiune && b.set_versiune && a.set_versiune !== b.set_versiune) return false;
        return true;
    };
    let prevGs: any = null;
    for (let i = gsHist.length - 2; i >= 0; i--) {
        if (_comparabile(lastGs, gsHist[i])) { prevGs = gsHist[i]; break; }
    }

    const kpiGs = document.getElementById('ai-mon-kpi-gs');
    const kpiDelta = document.getElementById('ai-mon-kpi-gs-delta');
    if (kpiGs) {
        kpiGs.textContent = lastGs?.score != null ? (lastGs.score * 100).toFixed(1) + '%' : '—';
        if (lastGs?.rulare_incompleta) kpiGs.textContent += ' ⚠';
    }
    if (kpiDelta) {
        if (lastGs?.rulare_incompleta) {
            kpiDelta.innerHTML = `<span style="color:var(--yellow,#ffd600)" title="Rularea s-a oprit înainte de capăt, deci a măsurat un subset întâmplător de întrebări. Nu se compară cu nimic.">rulare întreruptă: ${lastGs.total || 0} din ${lastGs.planificate || '?'} întrebări</span>`;
        } else if (lastGs && prevGs && lastGs.score != null && prevGs.score != null) {
            const d = (lastGs.score - prevGs.score) * 100;
            const color = d >= 0 ? 'var(--green)' : 'var(--red)';
            const arrow = d >= 0 ? '▲' : '▼';
            kpiDelta.innerHTML = `<span style="color:${color}">${arrow} ${Math.abs(d).toFixed(1)}pp</span> vs. rularea comparabilă`;
        } else if (lastGs) {
            kpiDelta.innerHTML = '<span style="color:var(--text-muted)" title="Nu există o rulare anterioară care să măsoare același lucru (același traseu, același număr de întrebări, aceeași versiune de set).">fără rulare comparabilă</span>';
        } else { kpiDelta.textContent = ''; }
    }

    // KPI Feedback totals
    const fb = data.feedback_totals || {};
    const kpiFb = document.getElementById('ai-mon-kpi-fb');
    const kpiFbSub = document.getElementById('ai-mon-kpi-fb-sub');
    if (kpiFb) kpiFb.textContent = String(fb.total || 0);
    if (kpiFbSub) kpiFbSub.textContent = `👍 ${fb.good || 0}  👎 ${fb.bad || 0}  ✏️ ${fb.correction || 0}`;

    // KPI Chat 7d
    const cs = data.chat_stats || {};
    const kpiChat = document.getElementById('ai-mon-kpi-chat');
    const kpiChatSub = document.getElementById('ai-mon-kpi-chat-sub');
    if (kpiChat) kpiChat.textContent = String(cs.last_7_days || 0);
    if (kpiChatSub) {
        const bysrc = cs.by_source_7d || {};
        const entries = Object.entries(bysrc).map(([k, v]) => `${k}: ${v}`).join(' · ');
        kpiChatSub.textContent = entries || '—';
    }

    // KPI Cache
    const cache = data.cache_stats || {};
    const kpiCache = document.getElementById('ai-mon-kpi-cache');
    const kpiCacheSub = document.getElementById('ai-mon-kpi-cache-sub');
    if (kpiCache) kpiCache.textContent = String(cache.entries || 0);
    if (kpiCacheSub) kpiCacheSub.textContent = `Hits totale: ${cache.total_hits || 0}`;

    // GS history table
    const gsTable = document.getElementById('ai-mon-gs-history');
    if (gsTable) {
        if (gsHist.length === 0) {
            gsTable.innerHTML = '<span style="color:var(--text-muted)">Niciun scor înregistrat.</span>';
        } else {
            const rows = gsHist.slice().reverse().map((g: any) => {
                const ts = (g.timestamp || '').substring(0, 16);
                const score = g.score != null ? (g.score * 100).toFixed(1) + '%' : '—';
                const bar = g.score != null ? Math.max(0, Math.min(100, g.score * 100)) : 0;
                return `<div style="display:flex;align-items:center;gap:10px;padding:4px 0;border-bottom:1px solid var(--border)">
                    <span style="width:150px;color:var(--text-muted);font-size:.78rem">${escHtml(ts)}</span>
                    <div style="flex:1;background:var(--bg);height:16px;border-radius:3px;overflow:hidden">
                        <div style="width:${bar}%;height:100%;background:linear-gradient(90deg,var(--accent),var(--green))"></div>
                    </div>
                    <span style="width:60px;text-align:right;font-weight:600">${score}</span>
                    <span style="width:90px;text-align:right;color:var(--text-muted);font-size:.78rem">${g.pass || 0}/${g.total || 0}</span>
                    <span style="width:104px;text-align:right;font-size:.72rem;color:${g.rulare_incompleta ? 'var(--yellow,#ffd600)' : 'var(--text-muted)'}"
                        title="${g.rulare_incompleta ? 'Rulare întreruptă — a măsurat doar întrebările până unde a apucat' : escHtml(g.mod || '')}">${g.rulare_incompleta ? '⚠ întreruptă' : escHtml(g.mod || '')}</span>
                </div>`;
            }).join('');
            gsTable.innerHTML = rows;
        }
    }

    // Feedback trend
    const fbTrend = document.getElementById('ai-mon-fb-trend');
    if (fbTrend) {
        const trend = data.feedback_trend || [];
        if (trend.length === 0) {
            fbTrend.innerHTML = '<span style="color:var(--text-muted)">Niciun feedback în ultimele 8 săptămâni.</span>';
        } else {
            fbTrend.innerHTML = trend.map((w: any) =>
                `<div style="display:flex;gap:10px;padding:3px 0;border-bottom:1px dashed var(--border)">
                    <span style="width:90px;color:var(--text-muted);font-size:.8rem">${escHtml(w.week)}</span>
                    <span style="color:var(--green);width:70px">👍 ${w.good || 0}</span>
                    <span style="color:var(--red);width:70px">👎 ${w.bad || 0}</span>
                    <span style="color:#4f46e5;width:80px">✏️ ${w.correction || 0}</span>
                    <span style="color:var(--text-muted);margin-left:auto">Total: ${w.total || 0}</span>
                </div>`
            ).join('');
        }
    }

    // Pipeline latest
    const pipe = document.getElementById('ai-mon-pipeline');
    if (pipe) {
        pipe.textContent = data.pipeline_latest
            ? JSON.stringify(data.pipeline_latest, null, 2)
            : 'Niciun rulaj de pipeline înregistrat.';
    }
}

function _populateMachineSelects(): void {
    const machines = _allMachinesCache || [];
    const lines = new Map<string, string>();
    machines.forEach((m: any) => {
        if (m.line && m.id) lines.set(m.line, m.id);
    });
    const lineList = [
        'M1', 'M2', 'M3', 'M4', 'M5', 'M6', 'M7', 'M8', 'Utilități',
    ];
    ['gs-filter-machine', 'gs-run-machine'].forEach(selId => {
        const sel = document.getElementById(selId) as HTMLSelectElement | null;
        if (!sel) return;
        const existing = sel.value;
        while (sel.options.length > 1) sel.remove(1);
        lineList.forEach(line => {
            const opt = document.createElement('option');
            opt.value = line;
            opt.textContent = line;
            sel.appendChild(opt);
        });
        sel.value = existing;
    });
}

function _initEvents(): void {
    $('#gs-filter-machine')?.addEventListener('change', () => _renderQuestions());
    $('#gs-filter-difficulty')?.addEventListener('change', () => _renderQuestions());
    $('#gs-btn-add')?.addEventListener('click', _openAddDialog);
    $('#gs-btn-run')?.addEventListener('click', _startEvaluation);
}

/* ═══ QUESTIONS ═════════════════════════════════════════════ */

async function _loadQuestions(): Promise<void> {
    try {
        const resp = await api('/api/golden_set');
        _gsItems = resp.data || [];
        _renderQuestions();
    } catch (e: any) {
        toast('Eroare încărcare golden set: ' + e.message, 'error');
    }
}

function _renderQuestions(): void {
    const container = $('#gs-questions-list')!;
    const empty = $('#gs-questions-empty') as HTMLElement;
    const machineFilter = (document.getElementById('gs-filter-machine') as HTMLSelectElement)?.value || '';
    const diffFilter = (document.getElementById('gs-filter-difficulty') as HTMLSelectElement)?.value || '';

    let items = _gsItems;
    if (machineFilter) {
        const mu = machineFilter.toUpperCase();
        items = items.filter(i =>
            (i.line || '').toUpperCase() === mu ||
            (i.machine_id || '').toUpperCase().includes(mu)
        );
    }
    if (diffFilter) {
        items = items.filter(i => i.difficulty === diffFilter);
    }

    if (!items.length) {
        container.innerHTML = '';
        empty.style.display = '';
        return;
    }
    empty.style.display = 'none';

    const lastResults = new Map<string, GsResult>();
    _gsResults.forEach(r => lastResults.set(r.id, r));

    container.innerHTML = items.map(item => {
        const lr = lastResults.get(item.id);
        const gradeHtml = lr
            ? `<span class="gs-grade gs-grade-${lr.grade.toLowerCase()}">${lr.grade} ${Math.round(lr.score * 100)}%</span>`
            : '<span class="gs-grade gs-grade-none">—</span>';
        const kws = item.expected_keywords.map(k => `<span class="gs-kw">${escHtml(k)}</span>`).join('');
        const expAnswer = item.expected_answer
            ? `<details class="gs-q-expected"><summary>📗 Răspuns corect (referință)</summary><div class="gs-q-expected-body">${escHtml(item.expected_answer)}</div></details>`
            : '';
        const aiAnswer = lr
            ? `<details class="gs-q-ai-answer${lr.grade === 'FAIL' ? ' gs-q-ai-fail' : ''}"><summary>🤖 Răspuns AI (${lr.grade} ${Math.round(lr.score * 100)}%)</summary><div class="gs-q-ai-body">${escHtml(lr.answer_snippet)}</div></details>`
            : '';
        return `
        <div class="gs-question-card" data-id="${item.id}">
            <div class="gs-q-header">
                <span class="gs-q-id">${item.id}</span>
                <span class="gs-q-diff">${DIFF_LABELS[item.difficulty] || item.difficulty}</span>
                <span class="gs-q-chapter">${CHAPTER_LABELS[item.chapter_type] || item.chapter_type}</span>
                <span class="gs-q-machine">${item.line || item.machine_id || '(general)'}</span>
                ${gradeHtml}
                <button class="gs-q-del" data-del="${item.id}" title="Șterge">✕</button>
            </div>
            <div class="gs-q-text">${escHtml(item.question)}</div>
            <div class="gs-q-keywords">${kws}</div>
            ${expAnswer}
            ${aiAnswer}
        </div>`;
    }).join('');

    container.querySelectorAll('.gs-q-del').forEach(btn => {
        btn.addEventListener('click', async (e) => {
            e.stopPropagation();
            const id = (btn as HTMLElement).dataset.del!;
            if (!await askConfirm(`Ștergi întrebarea ${id}?`)) return;
            try {
                await api(`/api/golden_set/${id}`, { method: 'DELETE' });
                toast('Întrebare ștearsă', 'success');
                await _loadQuestions();
            } catch (err: any) {
                toast('Eroare: ' + err.message, 'error');
            }
        });
    });
}

function _openAddDialog(): void {
    const existing = document.getElementById('gs-add-overlay');
    if (existing) existing.remove();

    const overlay = document.createElement('div');
    overlay.id = 'gs-add-overlay';
    overlay.className = 'config-modal-overlay';
    overlay.innerHTML = `
    <div class="config-modal" style="max-width:600px">
        <div class="config-modal-header">
            <h3>Adaugă întrebare Golden Set</h3>
            <button class="config-modal-close" id="gs-add-close">&times;</button>
        </div>
        <div class="config-modal-body" style="display:flex;flex-direction:column;gap:12px">
            <div>
                <label class="dx-label">Întrebare *</label>
                <textarea id="gs-add-question" class="dx-input" rows="3" placeholder="Care este intervalul de..."></textarea>
            </div>
            <div style="display:flex;gap:12px">
                <div style="flex:1">
                    <label class="dx-label">Machine ID</label>
                    <input id="gs-add-machine" class="dx-input" placeholder="ex: IMM M1  CAPAC 48">
                </div>
                <div style="flex:1">
                    <label class="dx-label">Tip capitol</label>
                    <select id="gs-add-chapter" class="dx-input">
                        <option value="maintenance">Mentenanță</option>
                        <option value="error_codes">Coduri eroare</option>
                        <option value="adjustment">Reglaje</option>
                        <option value="spare_parts">Piese schimb</option>
                        <option value="safety">Siguranță</option>
                        <option value="operation">Operare</option>
                    </select>
                </div>
            </div>
            <div style="display:flex;gap:12px">
                <div style="flex:1">
                    <label class="dx-label">Dificultate</label>
                    <select id="gs-add-diff" class="dx-input">
                        <option value="easy">Easy</option>
                        <option value="medium" selected>Medium</option>
                        <option value="hard">Hard</option>
                    </select>
                </div>
                <div style="flex:1">
                    <label class="dx-label">Sursa așteptată</label>
                    <select id="gs-add-source" class="dx-input">
                        <option value="document_extracts">document_extracts</option>
                        <option value="maintenance_intervals">maintenance_intervals</option>
                        <option value="error_codes">error_codes</option>
                        <option value="parts">parts</option>
                        <option value="machines">machines</option>
                    </select>
                </div>
            </div>
            <div>
                <label class="dx-label">Răspuns corect (referință)</label>
                <textarea id="gs-add-answer" class="dx-input" rows="3" placeholder="Răspunsul corect extras din documentație..."></textarea>
            </div>
            <div>
                <label class="dx-label">Cuvinte cheie așteptate (separate prin virgulă)</label>
                <input id="gs-add-keywords" class="dx-input" placeholder="lunar, 720, filtru, apă">
            </div>
            <button id="gs-add-save" class="dx-btn-primary" style="align-self:flex-end;margin-top:8px">Salvează</button>
        </div>
    </div>`;
    document.body.appendChild(overlay);

    $('#gs-add-close')!.addEventListener('click', () => overlay.remove());
    overlay.addEventListener('click', (e) => { if (e.target === overlay) overlay.remove(); });

    $('#gs-add-save')!.addEventListener('click', async () => {
        const question = (document.getElementById('gs-add-question') as HTMLTextAreaElement).value.trim();
        const machine_id = (document.getElementById('gs-add-machine') as HTMLInputElement).value.trim();
        const chapter_type = (document.getElementById('gs-add-chapter') as HTMLSelectElement).value;
        const difficulty = (document.getElementById('gs-add-diff') as HTMLSelectElement).value;
        const expected_source = (document.getElementById('gs-add-source') as HTMLSelectElement).value;
        const expected_answer = (document.getElementById('gs-add-answer') as HTMLTextAreaElement).value.trim();
        const keywordsRaw = (document.getElementById('gs-add-keywords') as HTMLInputElement).value;
        const expected_keywords = keywordsRaw.split(',').map(k => k.trim()).filter(Boolean);

        if (!question) { toast('Completează întrebarea', 'error'); return; }
        if (!expected_keywords.length) { toast('Adaugă cel puțin un cuvânt cheie', 'error'); return; }

        try {
            await api('/api/golden_set', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ question, machine_id, chapter_type, difficulty, expected_source, expected_answer, expected_keywords }),
            });
            toast('Întrebare adăugată', 'success');
            overlay.remove();
            await _loadQuestions();
        } catch (err: any) {
            toast('Eroare: ' + err.message, 'error');
        }
    });
}

/* ═══ EVALUATION ═══════════════════════════════════════════ */

async function _startEvaluation(): Promise<void> {
    const machine = (document.getElementById('gs-run-machine') as HTMLSelectElement)?.value || '';
    const difficulty = (document.getElementById('gs-run-difficulty') as HTMLSelectElement)?.value || '';

    const btnRun = $('#gs-btn-run') as HTMLButtonElement;
    const btnStop = $('#gs-btn-stop') as HTMLButtonElement;
    btnRun.style.display = 'none';
    btnStop.style.display = 'inline-block';

    const progressWrap = $('#gs-run-progress') as HTMLElement;
    progressWrap.style.display = 'block';
    const fill = $('#gs-progress-fill') as HTMLElement;
    fill.style.width = '0%';
    fill.textContent = '';
    const live = $('#gs-run-live')!;
    live.innerHTML = '<div style="color:var(--text-muted);font-size:.85rem">Se pornește evaluarea...</div>';

    try {
        await api('/api/golden_set/run', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ machine, difficulty }),
        });
        _startPolling();
    } catch (err: any) {
        toast('Eroare: ' + err.message, 'error');
        btnRun.style.display = 'inline-block';
        btnStop.style.display = 'none';
    }
}

function _startPolling(): void {
    if (_pollTimer) clearInterval(_pollTimer);
    _pollTimer = setInterval(_pollProgress, 2000);
}

async function _pollProgress(): Promise<void> {
    try {
        const p = await api('/api/golden_set/progress');
        const fill = $('#gs-progress-fill') as HTMLElement;
        const pct = p.total ? Math.round((p.current / p.total) * 100) : 0;
        fill.style.width = pct + '%';
        fill.textContent = `${p.current} / ${p.total}`;

        const live = $('#gs-run-live')!;
        if (p.results && p.results.length) {
            const last = p.results[p.results.length - 1];
            const emoji = { PASS: '✅', PARTIAL: '⚠️', FAIL: '❌' }[last.grade as string] || '?';
            const existingCount = live.querySelectorAll('.gs-live-item').length;
            if (p.results.length > existingCount) {
                for (let i = existingCount; i < p.results.length; i++) {
                    const r = p.results[i];
                    const em = { PASS: '✅', PARTIAL: '⚠️', FAIL: '❌' }[r.grade as string] || '?';
                    const div = document.createElement('div');
                    div.className = 'gs-live-item';
                    div.innerHTML = `<span style="font-weight:600">${em} ${r.id}</span>
                        <span class="gs-grade gs-grade-${r.grade.toLowerCase()}">${r.grade} ${Math.round(r.score * 100)}%</span>
                        <span style="color:var(--text-muted);font-size:.82rem;flex:1;overflow:hidden;text-overflow:ellipsis;white-space:nowrap">${escHtml(r.question.substring(0, 80))}</span>
                        <span style="font-size:.8rem;color:var(--text-muted)">${r.elapsed_s}s</span>`;
                    div.style.cssText = 'display:flex;gap:8px;align-items:center;padding:4px 8px;border-radius:6px;background:var(--surface)';
                    live.appendChild(div);
                    live.scrollTop = live.scrollHeight;
                }
            }
        }

        if (!p.running) {
            clearInterval(_pollTimer);
            _pollTimer = null;
            const btnRun = $('#gs-btn-run') as HTMLButtonElement;
            const btnStop = $('#gs-btn-stop') as HTMLButtonElement;
            btnRun.style.display = 'inline-block';
            btnStop.style.display = 'none';
            // serverul spune de pe 20 aug DACĂ rularea chiar s-a terminat sau a
            // murit pe drum (audit 2.23: „Evaluare completă!" peste 29/94);
            // toast-ul repetă adevărul lui, nu presupune
            if ((p as any).intrerupt) {
                toast((p as any).motiv || `Evaluarea s-a ÎNTRERUPT la ${p.current}/${p.total}. Rezultatele parțiale nu înlocuiesc referința.`, 'error');
            } else {
                toast(`Evaluare completă: ${p.current}/${p.total} întrebări.`, 'success');
            }
            _gsResults = p.results || [];
            _renderQuestions();
            _renderResults();
        }
    } catch { /* ignore polling errors */ }
}

/* ═══ RESULTS ══════════════════════════════════════════════ */

async function _loadLastResults(): Promise<void> {
    try {
        const resp = await api('/api/golden_set/results');
        _gsResults = resp.data || [];
        _renderResults();
    } catch { /* no previous results */ }
}

function _renderResults(): void {
    const summary = $('#gs-results-summary')!;
    const tableWrap = $('#gs-results-table-wrap')!;
    const empty = $('#gs-results-empty') as HTMLElement;

    if (!_gsResults.length) {
        summary.innerHTML = '';
        tableWrap.innerHTML = '';
        empty.style.display = 'block';
        return;
    }
    empty.style.display = 'none';

    const total = _gsResults.length;
    const passed = _gsResults.filter(r => r.grade === 'PASS').length;
    const partial = _gsResults.filter(r => r.grade === 'PARTIAL').length;
    const failed = _gsResults.filter(r => r.grade === 'FAIL').length;
    const avgScore = _gsResults.reduce((s, r) => s + r.score, 0) / total;
    const avgTime = _gsResults.reduce((s, r) => s + r.elapsed_s, 0) / total;
    const ts = _gsResults[0]?.timestamp ? formatDateTimeRO(_gsResults[0].timestamp) : '';

    summary.innerHTML = `
    <div class="gs-summary-grid">
        <div class="gs-summary-card"><div class="gs-summary-val">${total}</div><div class="gs-summary-label">Total</div></div>
        <div class="gs-summary-card"><div class="gs-summary-val" style="color:#4caf50">${passed}</div><div class="gs-summary-label">✅ PASS</div></div>
        <div class="gs-summary-card"><div class="gs-summary-val" style="color:#ff9800">${partial}</div><div class="gs-summary-label">⚠️ PARTIAL</div></div>
        <div class="gs-summary-card"><div class="gs-summary-val" style="color:#f44336">${failed}</div><div class="gs-summary-label">❌ FAIL</div></div>
        <div class="gs-summary-card"><div class="gs-summary-val">${Math.round(avgScore * 100)}%</div><div class="gs-summary-label">Scor mediu</div></div>
        <div class="gs-summary-card"><div class="gs-summary-val">${avgTime.toFixed(1)}s</div><div class="gs-summary-label">Timp mediu</div></div>
    </div>
    ${ts ? `<p style="font-size:.82rem;color:var(--text-muted);margin-top:8px">Ultima evaluare: ${ts}</p>` : ''}`;

    let rows = _gsResults.map(r => {
        const missing = r.missing_keywords.slice(0, 3).join(', ');
        const moreCount = r.missing_keywords.length > 3 ? ` (+${r.missing_keywords.length - 3})` : '';
        return `<tr>
            <td>${r.id}</td>
            <td>${r.line || r.machine_id || '—'}</td>
            <td>${CHAPTER_LABELS[r.chapter_type] || r.chapter_type}</td>
            <td>${DIFF_LABELS[r.difficulty] || r.difficulty}</td>
            <td style="max-width:300px;overflow:hidden;text-overflow:ellipsis;white-space:nowrap">${escHtml(r.question.substring(0, 80))}</td>
            <td class="gs-grade gs-grade-${r.grade.toLowerCase()}">${r.grade} ${Math.round(r.score * 100)}%</td>
            <td style="font-size:.78rem;color:var(--text-muted)">${missing}${moreCount}</td>
            <td>${r.elapsed_s}s</td>
        </tr>`;
    }).join('');

    tableWrap.innerHTML = `
    <table class="gs-results-table">
        <thead><tr>
            <th>ID</th><th>Utilaj</th><th>Capitol</th><th>Dif.</th><th>Întrebare</th><th>Scor</th><th>Lipsă</th><th>Timp</th>
        </tr></thead>
        <tbody>${rows}</tbody>
    </table>`;
}
